<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("comunicados.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="createComunicado()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Tipo" :caption="$t('comunicados.grid.tipo')" cell-template="tipoComunicadoTemplate">
        <DxLookup v-bind="getLookup('comunicado', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="Estado" :caption="$t('comunicados.grid.estado')" cell-template="estadoComunicadoTemplate">
        <DxLookup v-bind="getLookup('comunicado', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Asunto" :caption="$t('comunicados.grid.asunto')"></DxColumn>
      <DxColumn data-field="TipoEmpresaAgenteDirigido" :caption="$t('comunicados.grid.tipo_empresa_agente_dirigido')"
        cell-template="tipoEmpresaAgenteDirigidoTemplate" :filter-operations="['contains']"
        :calculate-filter-expression="calculateTipoEmpresaAgenteDirigidoFilterExpression">
      </DxColumn>
      <DxColumn data-field="Roles" :caption="$t('usuarios.grid.rol')" cell-template="rolesTemplate" :filter-operations="['contains']"
        :calculate-filter-expression="calculateRolesFilterExpression">
      </DxColumn>
      <DxColumn data-field="EmpresasAgentes" :caption="$t('comunicados.grid.empresas_agentes')" cell-template="empresasAgentesTemplate"
        :filter-operations="['contains']" :calculate-filter-expression="calculateEmpresasAgentesFilterExpression">
      </DxColumn>

      <DxColumn data-field="FechaCreacion" :caption="$t('comunicados.grid.fecha_creacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaEnvio" :caption="$t('comunicados.grid.fecha_envio')" data-type="datetime" format="dd/MM/yyyy HH:mm"></DxColumn>

      <DxColumn type="buttons" :caption="$t('empresas_inmobiliarias.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('comunicados.grid.editar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => editar(e.row?.data.Id),
          visible: (options: any) => options.row?.data.PuedeEditar === true
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('comunicados.grid.ver_detalle'),
          icon: 'fa-regular fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id),
          visible: (options: any) => options.row?.data.Estado === EstadoComunicadoEnum.Enviado
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('comunicados.grid.previsualizar'),
          icon: 'fa-regular fa-eye text-success',
          onClick: (e: any) => previsualizar(e.row?.data.Id)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('comunicados.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.PuedeEliminarse === false
        },
      ]" />

      <template #tipoComunicadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `tipo-comunicado ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`comunicado.tipo.${data.value}`) }}
        </div>
      </template>

      <template #estadoComunicadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-comunicado ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`comunicado.estado.${data.value}`) }}
        </div>
      </template>

      <template #tipoEmpresaAgenteDirigidoTemplate="{ data }">
        <div v-if="esValorTodos(data.value)" class="d-inline-block rounded py-1 px-2 tipoEmpresaAgente TODAS">
          {{ $t("comunicados.grid.todos") }}
        </div>
        <div v-else :class="[
          `d-inline-block rounded py-1 px-2`,
          `tipoEmpresaAgente ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>

      <template #rolesTemplate="{ data }">
        <div v-if="esValorTodos(data.value)" class="roles">
          <div class="d-inline-block rounded py-1 px-2 rol TODOS">
            {{ $t("comunicados.grid.todos") }}
          </div>
        </div>
        <div v-else class="roles">
          <div v-for="role in (data.value || '').split(',').map((role: string) => role.trim()).filter(Boolean)" :key="role"
            class="d-inline-block rounded py-1 px-2 me-1" :class="`rol ${role}`">
            {{ $t(`usuarios.rol.${role}`) }}
          </div>
        </div>
      </template>

      <template #empresasAgentesTemplate="{ data }">
        <div v-if="esValorTodos(data.value)" class="empresas-agentes">
          <div class="d-inline-block rounded py-1 empresa-agente todas">
            {{ $t("comunicados.grid.todas") }}
          </div>
        </div>
        <div v-else class="empresas-agentes" :title="obtenerEmpresasAgentesLista(data.value).join(', ')">
          <template v-for="(empresaAgente, index) in obtenerEmpresasAgentesVisibles(data.value)" :key="empresaAgente">
            <span v-if="index !== 0">, </span>
            <span class="d-inline-block rounded py-1" :class="`empresa-agente ${empresaAgente}`">
              {{ empresaAgente }}
            </span>
          </template>
          <span v-if="tieneMasEmpresasAgentes(data.value)">...</span>
        </div>
      </template>
    </DxDataGrid>

  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import { lgModalPopupProps } from "@helpers/modal-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import ComunicadoForm from "@/components/comunicados/ComunicadoForm.vue";
import ComunicadoDetail from "@/components/comunicados/ComunicadoDetail.vue";
import ComunicadoPrevisualizacion from "@components/comunicados/ComunicadoPrevisualizacion.vue";
import { ComunicadoDetailModel } from "@/components/comunicados/models/ComunicadoDetailModel";
import { ComunicadoPrevisualizacionModel } from "@components/comunicados/models/ComunicadoPrevisualizacionModel";
import { ComunicadoModel } from "@/components/comunicados/models/ComunicadoModel";
import { EstadoComunicadoEnum, type DXLoadOptions } from "@/.shared/.api";
import { useRouter } from '@services/routing-services';
import { toastError, toastSuccess } from "@/.shared/services/notification-service";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const router = useRouter();

const esValorTodos = (valor: string | null | undefined): boolean => valor == null || valor === "";

const MAX_EMPRESAS_AGENTES_VISIBLES = 3;

function obtenerEmpresasAgentesLista(valor: string | null | undefined): string[] {
  return (valor || "").split(",").map((empresaAgente) => empresaAgente.trim()).filter(Boolean);
}

function obtenerEmpresasAgentesVisibles(valor: string | null | undefined): string[] {
  return obtenerEmpresasAgentesLista(valor).slice(0, MAX_EMPRESAS_AGENTES_VISIBLES);
}

function tieneMasEmpresasAgentes(valor: string | null | undefined): boolean {
  return obtenerEmpresasAgentesLista(valor).length > MAX_EMPRESAS_AGENTES_VISIBLES;
}

function crearFiltroTodos(field: string, filterValue: string | null) {
  if (filterValue == null || filterValue === "") {
    return [
      [field, "=", null],
      "or",
      [field, "=", ""],
    ];
  }
  return [field, "=", filterValue];
}

const calculateTipoEmpresaAgenteDirigidoFilterExpression = (filterValue: string) => {
  if (coincideConTextoEtiqueta(filterValue, intl.i18n.t("comunicados.grid.todos"))) {
    return crearFiltroTodos("TipoEmpresaAgenteDirigido", "");
  }
  return [["TipoEmpresaAgenteDirigido", "contains", filterValue.toUpperCase()]];
};

function coincideConTextoEtiqueta(filterValue: string, textoEtiqueta: string): boolean {
  return filterValue.trim().toUpperCase() === textoEtiqueta.trim().toUpperCase();
}

const calculateRolesFilterExpression = (filterValue: string) => {
  if (coincideConTextoEtiqueta(filterValue, intl.i18n.t("comunicados.grid.todos"))) {
    return crearFiltroTodos("Roles", "");
  }
  return [["Roles", "contains", filterValue.toUpperCase()]];
};

const calculateEmpresasAgentesFilterExpression = (filterValue: string) => {
  if (coincideConTextoEtiqueta(filterValue, intl.i18n.t("comunicados.grid.todas"))) {
    return crearFiltroTodos("EmpresasAgentes", "");
  }
  return [["EmpresasAgentes", "contains", filterValue]];
};

onMounted(async () => {
  setDataSource();
});

const createComunicado = async () => {
  const title = `${intl.i18n.t("comunicados.creacion.titulo")}`;
  modal.showModal(
    ComunicadoForm,
    { model: new ComunicadoModel(), isEditMode: false },
    { ...lgModalPopupProps, title },
  ).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
};

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;

      return await api.callStandardApi("getComunicadosForGrid", { showLoading: false }, q);
    }
  });
};

const verDetalle = async (id: string) => {
  const detail = await api.callStandardApi("getComunicadoDetail", { showLoading: true }, id);
  const title = intl.i18n.t("comunicados.detalle.titulo");
  modal.showModal(
    ComunicadoDetail,
    { model: ComunicadoDetailModel.fromApi(detail) },
    { ...lgModalPopupProps, title },
  );
};

const previsualizar = async (id: string) => {
  const detail = await api.callStandardApi("getComunicadoDetail", { showLoading: true }, id);
  const title = intl.i18n.t("comunicados.previsualizacion.titulo");
  modal.showModal(
    ComunicadoPrevisualizacion,
    { model: ComunicadoPrevisualizacionModel.fromApi(detail) },
    { ...lgModalPopupProps, title },
  );
};

const editar = async (id: string) => {
  const detail = await api.callStandardApi("getComunicadoDetail", { showLoading: true }, id);
  const title = intl.i18n.t("comunicados.edicion.titulo");
  modal.showModal(
    ComunicadoForm,
    { model: ComunicadoModel.fromDetail(detail), isEditMode: true },
    { ...lgModalPopupProps, title },
  ).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
};

const borrar = async (id: string) => {
  const respuesta = await modal.messageBox(
    intl.i18n.t("comunicados.grid.borrar_pregunta"),
    intl.i18n.t("comunicados.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );

  if (respuesta == messageBoxResult.ok) {
    await api
      .callStandardApi("deleteComunicado", { showLoading: true }, id)
      .then(() => {
        toastSuccess(intl.i18n.t("comunicados.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("comunicados.borrado_fallo")));
  }
};

</script>

<style lang="scss" scoped>
.roles span:nth-child(n + 2) {
  margin-top: 5px;
}

.empresas-agentes {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
